/*!
 * Copyright © 2024. Verizon Connect Ireland Limited. All rights reserved.
 */

import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { IVideoEventHttpService } from './video-event-http.service.interface';
import { EVideoClassifications, EVideoEventGenerationType, IHarshDrivingEvent, IVideoEvent, IVideoEventDetails } from '../../models';
import { IVideoEventListQueryRequest } from '../../models/video-event-list-query-request.interface';
import { environment } from '../../../../environments/environment';
import { IVideoEventVodRequest } from '../../models/video-event-vod-request.interface';

@Injectable()
export class VideoEventHttpService implements IVideoEventHttpService {
  constructor(private readonly _httpClient: HttpClient) {}

  getEvent(eventId: number): Observable<IHarshDrivingEvent> {
    return this._httpClient.get<IHarshDrivingEvent>(`${environment.config.endpoints.videoEventApiEndpoint}/v1/events/${eventId}`);
  }

  getVideoEvents(start: Date, end: Date, totalDays: number, vehicleId: number): Observable<IVideoEvent[]> {
    const body: IVideoEventListQueryRequest = {
      eventStartTimeUtc: start,
      eventEndTimeUtc: end,
      classifications: [
        EVideoClassifications.Collision,
        EVideoClassifications.Dangerous,
        EVideoClassifications.HarshDriving,
        EVideoClassifications.LowRisk,
        EVideoClassifications.Requested
      ],
      eventGenerationTypes: [
        EVideoEventGenerationType.VTU,
        EVideoEventGenerationType.VoD,
        EVideoEventGenerationType.ADAS,
        EVideoEventGenerationType.DMS
      ],
      vehicleIds: [vehicleId],
      limit: 100 * totalDays
    };
    return this._httpClient
      .post<{ data: IVideoEvent[] }>(`${environment.config.endpoints.videoEventApiEndpoint}/v1/events/query`, body)
      .pipe(
        map(response =>
          response.data.map(responseDetail => ({
            ...responseDetail,
            timeUtc: new Date(responseDetail.timeUtc)
          }))
        )
      );
  }

  postVodEvents(start: Date, end: Date, vehicleId: number): Observable<HttpResponse<any>> {
    const body: IVideoEventVodRequest = {
      startTimeUtc: start,
      endTimeUtc: end,
      vehicleId: vehicleId,
      online: false
    };
    return this._httpClient.post<any>(`${environment.config.endpoints.videoEventApiEndpoint}/v1/events`, body);
  }

  getVideoDetails(videoKey: string): Observable<IVideoEventDetails[]> {
    return this._httpClient.get<IVideoEventDetails[]>(`${environment.config.endpoints.videoEventApiEndpoint}/v1/events/${videoKey}/videos`);
  }

  recordEventView(eventKey: string): Observable<void> {
    const body = { timeUTC: new Date().toISOString() };
    return this._httpClient.post<void>(`${environment.config.endpoints.videoEventApiEndpoint}/v1/events/${eventKey}/views`, body);
  }
}
